import type { TenantJourneyStageId } from "@/lib/tenantJourney";

export type BrainCategory =
  "payments" | "tenants" | "owners" | "properties" | "support";
export const BRAIN_CATEGORIES: readonly BrainCategory[] = [
  "payments",
  "tenants",
  "owners",
  "properties",
  "support",
];

export type BrainPriority = "urgent" | "high" | "normal" | "low";
export const BRAIN_PRIORITIES: readonly BrainPriority[] = [
  "urgent",
  "high",
  "normal",
  "low",
];

export type BrainKind =
  // payments
  | "payout_ready"
  | "reconcile"
  | "late_rent"
  // tenants
  | "application_review"
  | "application_incomplete"
  | "stage_signature_pending"
  | "stage_deposit_pending"
  | "stage_key_pickup_pending"
  | "stage_handover_pending"
  | "stage_status_ahead"
  | "stage_moveout_scheduled"
  | "stage_moveout_protocol_pending"
  | "stage_key_return_pending"
  | "stage_deposit_refund_pending"
  | "room_base_drift"
  | "tenant_details_missing"
  | "empty_rooms"
  // checkout protocol (Neel Slack DM 2026-10-19): a tenant requested a
  // move-out date, awaiting admin confirm/reject. Independent of the
  // stage_* ladder — a request can exist while the journey is still Active.
  | "move_out_requested"
  // owners
  | "onboarding_review"
  | "owner_invite_unsigned"
  | "property_submission"
  | "lease_end_approaching"
  // properties
  | "owner_bank_missing"
  | "representor_missing"
  | "property_setup_missing"
  // support
  | "ticket_unassigned"
  | "ticket_unread"
  | "ticket_stale";

/** A translatable string: always rendered as t(key, fallback). */
export interface BrainText {
  key: string;
  fallback: string;
}

export interface BrainLink {
  /** Plain data (a person's or property's name) — never translated. */
  label: string;
  href: string;
}

/** A card fact. Exactly one of value / parts / date is set. */
export interface BrainFact {
  label: BrainText;
  /** Plain data (an amount, a name, a count) — never translated. */
  value?: string;
  /** Translated words, rendered t() each and joined with ", ". */
  parts?: BrainText[];
  /** YYYY-MM-DD or ISO timestamp, formatted in the reader's language. */
  date?: string;
  /** YYYY-MM, shown as the month in the reader's language. */
  month?: string;
  /** Euros, formatted in the reader's language. With `month`: "July 2026 · €600.00". */
  eur?: number;
}

export interface BrainRefs {
  bookingId?: number;
  userId?: string | null;
  email?: string | null;
  personName?: string | null;
  ticketId?: string;
  matchId?: string;
  lessorId?: string;
  propertyId?: number;
  apartmentId?: number | null;
  roomId?: number;
}

/** What a derive function returns: one open thing that needs a human. */
export interface BrainItem {
  /** Deterministic from the source row: `${kind}:${entityId}`; roll-ups are just `${kind}`. */
  key: string;
  kind: BrainKind;
  /** ISO timestamp the condition began (drives age + escalation). null = unknown → age 0. */
  since: string | null;
  who?: BrainLink;
  where?: BrainLink;
  /** One plain-data line under the title (names, amounts). Not translated. */
  summary?: string;
  facts: BrainFact[];
  /** The deep link the primary "Open" goes to (without the brain return params). */
  primaryHref: string;
  /** Stage pill, same as the tenant profile. */
  stageId?: TenantJourneyStageId;
  /**
   * Only for kinds whose source row carries its own assignee (tickets).
   * undefined = Brain-owned assignment from brain_item_state.
   */
  sourceAssigneeId?: string | null;
  /** "Last reminded" from automatic reminders, when the source records one. */
  lastRemindedAt?: string | null;
  refs: BrainRefs;
  /** Kind-specific data for in-card actions. */
  payout?: {
    lessorId: string;
    propertyId: number;
    periodMonth: string;
    payoutEur: number;
    collectedEur: number;
    bankHolder: string | null;
    bankIban: string | null;
    bankBic: string | null;
    /**
     * The unpaid offset invoices `payoutEur` is the payout FOR (COL-516,
     * OwnerPayoutGroup.unpaidInvoiceIds). Empty = unknown, never "none".
     */
    unpaidInvoiceIds: string[];
  };
  recon?: {
    matchId: string;
    amountCents: number;
    suggestedPaymentId: string | null;
    counterpartName: string | null;
    purpose: string | null;
    reviewReason: string | null;
  };
  drift?: {
    bookingRent: number | null;
    roomRent: number | null;
    bookingDeposit: number | null;
    roomDeposit: number | null;
  };
  /**
   * owner_invite_unsigned: the properties still at invite_sent. `resendable`
   * = a property-wide invite is safe from the card (see derive-owners);
   * otherwise the owner page, which can scope it to an apartment.
   */
  invitedProperties?: {
    id: number;
    title: string | null;
    resendable: boolean;
  }[];
  missingFields?: string[];
  /** late_rent: one row per late tenant. */
  lateRent?: {
    bookingId: number;
    tenant: string | null;
    property: string | null;
    openEur: number;
    feesEur: number;
    /** ISO date of the oldest open rent: the date rent is due from. */
    dueSince: string | null;
  }[];
  documents?: { type: string; fileUrl: string; status: string | null }[];
  rollupCount?: number;
  /** move_out_requested: the pending move_out_date_requests row. */
  moveOutRequest?: {
    requestId: string;
    requestedDate: string;
    /** `bookings.end_date` — the contract's earliest possible move-out. */
    earliestDate: string | null;
    /** `bookings.move_out_notice_at` — kept on confirm if already set,
     *  never stamped over. */
    existingNoticeAt: string | null;
  };
}

/** A row of public.brain_item_state. */
export interface BrainItemState {
  item_key: string;
  assignee_id: string | null;
  assign_note: string | null;
  priority_override: BrainPriority | null;
  last_emailed_at: string | null;
}

/** A row of public.brain_item_notes: shared, append-only. */
export interface BrainItemNote {
  id: number;
  item_key: string;
  author_id: string | null;
  note: string;
  created_at: string;
}

export interface DeckCard extends BrainItem {
  category: BrainCategory;
  priority: BrainPriority;
  priorityReason: BrainText;
  manualPriority: boolean;
  /** Effective assignee: the ticket's own, else brain_item_state's, else null (Everyone). */
  assigneeId: string | null;
  assignNote: string | null;
  ageDays: number;
  lastEmailedAt: string | null;
  ackNote?: string | null;
}

export type BrainScope = "all" | "mine" | "everyone";
